import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Heart, User, Sparkles, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getPlanMeta } from '@/lib/plans';
import { ProfileCompletion } from '@/components/ProfileCompletion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = ({ user }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [profileStatus, setProfileStatus] = useState(user?.profile || null);

  useEffect(() => {
    fetchSubscription();
    fetchDailyMatches();
    fetchProfileStatus();
  }, []);

  const fetchProfileStatus = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      setProfileStatus(res.data.profile);
    } catch (e) { /* non-blocking */ }
  };

  const fetchSubscription = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/payments/subscription`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const fetchDailyMatches = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/matches/daily`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchAction = async (matchId, action) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.post(`${API}/matches/${matchId}/action`, { action }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      toast.success(action === 'accept' ? 'Match accepted!' : 'Match declined');
      setMatches(matches.filter(m => m.match.id !== matchId));
      setSelectedMatch(null);
    } catch (error) {
      console.error('Match action failed:', error);
      toast.error('Action failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="animate-pulse text-2xl font-light text-rose-600">Loading your matches...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-5xl font-bold text-rose-900 mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Hello, {user.name}!
          </h1>
          <p className="text-lg text-gray-600">
            Here are your daily intentional matches
            <span className="ml-2 text-rose-600 font-medium">({getPlanMeta(subscription?.plan_type).matchesLabel})</span>
          </p>
          
          {subscription?.plan_type === 'free' && profileStatus?.match_ready && matches.length >= 5 && (
            <Card className="mt-4 bg-gradient-to-r from-amber-50 to-rose-50 border-amber-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <p className="text-sm text-gray-700">
                    You've reached your daily limit. <span className="font-semibold text-rose-700">Upgrade to Premium</span> for unlimited matches!
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/pricing')}
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white"
                >
                  Upgrade
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        <ProfileCompletion profile={profileStatus} onGo={() => navigate('/profile')} />

        {profileStatus && !profileStatus.match_ready ? (
          <Card className="shadow-lg border-rose-200" data-testid="match-gate-card">
            <CardContent className="p-12 text-center">
              <Heart className="w-16 h-16 text-rose-300 mx-auto mb-4 fill-rose-200" />
              <h3 className="text-2xl font-semibold text-rose-900 mb-2">Finish your profile to unlock matches</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                We match on values, not swipes. Add {profileStatus.missing_essentials?.slice(0, 3).join(', ') || 'a few more details'} so we can find people truly aligned with you.
              </p>
              <Button
                data-testid="unlock-matches-btn"
                onClick={() => navigate('/profile')}
                className="mt-6 bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white"
              >
                Complete my profile
              </Button>
            </CardContent>
          </Card>
        ) : matches.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-16 h-16 text-rose-300 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-rose-900 mb-2">No more matches today</h3>
              <p className="text-gray-600">Check back tomorrow for fresh connections!</p>
              <Button
                data-testid="view-matches-btn"
                onClick={() => navigate('/matches')}
                className="mt-6 bg-rose-500 hover:bg-rose-600"
              >
                View Accepted Matches
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.map(({ match, user: matchUser }) => (
              <Card
                key={match.id}
                data-testid={`match-card-${match.id}`}
                className="overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border-rose-100 cursor-pointer"
                onClick={() => setSelectedMatch({ match, user: matchUser })}
              >
                <div className="h-64 bg-gradient-to-br from-rose-200 to-amber-200 flex items-center justify-center">
                  {matchUser.picture ? (
                    <img src={matchUser.picture} alt={matchUser.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-24 h-24 text-rose-400" />
                  )}
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                    {matchUser.name}, {matchUser.date_of_birth ? new Date().getFullYear() - new Date(matchUser.date_of_birth).getFullYear() : '?'}
                  </CardTitle>
                  <CardDescription>
                    {matchUser.location?.city && `${matchUser.location.city}, `}
                    {matchUser.location?.country}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Compatibility</span>
                      <span className="text-lg font-bold text-rose-600">{Math.round(match.compatibility_score)}%</span>
                    </div>
                    <Progress value={match.compatibility_score} className="h-2" />
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-3">{matchUser.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Match Detail Modal */}
      {selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-3xl mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                    {selectedMatch.user.name}
                  </CardTitle>
                  <CardDescription>
                    {selectedMatch.user.location?.city}, {selectedMatch.user.location?.country}
                  </CardDescription>
                </div>
                <Button
                  data-testid="close-modal-btn"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedMatch(null)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold text-rose-900 mb-2">About</h4>
                <p className="text-gray-700">{selectedMatch.user.bio}</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-rose-900 mb-2">Compatibility Score</h4>
                <div className="flex items-center gap-4">
                  <Progress value={selectedMatch.match.compatibility_score} className="flex-1 h-3" />
                  <span className="text-2xl font-bold text-rose-600">{Math.round(selectedMatch.match.compatibility_score)}%</span>
                </div>
              </div>

              {selectedMatch.user.core_values?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-rose-900 mb-2">Core Values</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMatch.user.core_values.map((value) => (
                      <span key={value} className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm">
                        {value}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedMatch.user.relationship_goals && (
                <div>
                  <h4 className="font-semibold text-rose-900 mb-2">Looking For</h4>
                  <p className="text-gray-700 capitalize">{selectedMatch.user.relationship_goals}</p>
                </div>
              )}

              <div className="flex gap-4 pt-6">
                <Button
                  data-testid="reject-match-btn"
                  variant="outline"
                  className="flex-1 border-rose-200 text-rose-600 hover:bg-rose-50"
                  onClick={() => handleMatchAction(selectedMatch.match.id, 'reject')}
                >
                  <X className="w-4 h-4 mr-2" />
                  Decline
                </Button>
                <Button
                  data-testid="accept-match-btn"
                  className="flex-1 bg-rose-500 hover:bg-rose-600"
                  onClick={() => handleMatchAction(selectedMatch.match.id, 'accept')}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Accept Match
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;